import * as React from 'react';
import { FieldsType } from '../constants/FieldsType';
import { ControllerRenderProps, UseFormStateReturn } from 'react-hook-form';
import { Form, FormDropdown } from 'semantic-ui-react';
import { DateFormat } from '../enums/DateFormat';
import {
  DatePickerInput,
  FilterGroupItem,
  POSMultiTag,
} from '@publicisgroupe/p-os-components';
import { Field } from '../interfaces/Field';
import { capitalize } from './stringUtils';
import { XdmMultiselectDropdown } from '../components/XdmMultiselectDropdown';
import { StringsMultiselectDropdown } from '../components/StringsMultiselectDropdown';
import { XdmContextView } from '../components/XdmContextView/XdmContextView';
import { XdmContextItem } from '../components/XdmContextItem/XdmContextItem';
import { clsx } from 'clsx';
const dateTimeFormat: Intl.DateTimeFormatOptions = {
  day: DateFormat.numeric,
  month: DateFormat.numeric,
  year: DateFormat.numeric,
};

const booleanDropdownOptions = (isRequired?: boolean) => {
  const list: Array<{ key: string; text: string; value: boolean | string }> = [
    { key: 'yes', text: 'Yes', value: true },
    { key: 'no', text: 'No', value: false },
  ];

  return isRequired
    ? list
    : [{ key: 'null', text: '', value: 'undefined' }, ...list];
};

function getFieldForMultiSelect(
  fieldInfo: Field,
  field: ControllerRenderProps<any, never>,
  errorMessage?: string
) {
  if (!fieldInfo.isArray) {
    console.error(
      'MultiSelect field is not an array. Only array is supported!'
    );
    return <></>;
  }

  return (
    <StringsMultiselectDropdown
      required={fieldInfo.required}
      errorMessage={errorMessage}
      label={field.name}
      options={fieldInfo.multiSelect?.values || []}
      checkedOptions={field.value || []}
      onChange={field.onChange}
    />
  );
}

export const getFieldForSingleSelect = (
  fieldInfo: Field,
  field: ControllerRenderProps<any, never>
) => {
  const values = fieldInfo.singleSelect?.values ?? [];
  const options = values.map((option: string, index) => ({
    key: index,
    text: option,
    value: option,
  }));

  return (
    <FormDropdown
      placeholder={`Enter your ${fieldInfo.name}`}
      required={fieldInfo.required}
      selection
      search
      clearable
      label={capitalize(field.name)}
      value={field.value}
      onChange={(_, item) => field.onChange(item.value || '')}
      options={options}
    />
  );
};

function getFieldForXdmContext(
  fieldInfo: Field,
  field: ControllerRenderProps<any, never>,
  fieldLabel: string,
  errorMessage?: string
) {
  if (!fieldInfo.xdmContext) {
    return <></>;
  }
  return (
    <div
      className={clsx({ required: fieldInfo.required, error: errorMessage })}
    >
      <label className="required-label">{fieldLabel}</label>
      <XdmContextView
        field={fieldInfo}
        value={field.value}
        readonly={false}
        onValueChanged={(newValue) => field.onChange(newValue)}
      />
    </div>
  );
}

function getXdmContextItem(
  fieldInfo: Field,
  field: ControllerRenderProps<any, never>,
  fieldLabel: string,
  initialValues?: any,
  onCustomFieldChange?: (
    filterItems: FilterGroupItem[],
    selectedGroupId: string
  ) => void
) {
  if (!fieldInfo.xdmContext) {
    return <></>;
  }
  return (
    <XdmContextItem
      field={fieldInfo}
      initialValues={field.value || initialValues || {}}
      header={fieldLabel}
      isFilterItem
      onChange={(newValue) => {
        field.onChange(newValue);
        onCustomFieldChange?.(newValue, fieldInfo.name);
      }}
    />
  );
}

const getClassName = (field: ControllerRenderProps<any, never>) =>
  clsx({
    filled: field.value === true || field.value === false,
  });

export const getField = (
  fieldInfo: Field,
  field: ControllerRenderProps<any, never>,
  formState: UseFormStateReturn<Field>,
  isForFiltering?: boolean,
  onCustomFieldChange?: (filterItems: any[], selectedGroupId: string) => void,
  defaultValues?: any
) => {
  const fieldLabel = capitalize(fieldInfo.name);

  const handleBoolean = (
    event: React.SyntheticEvent<HTMLElement, Event>,
    data: any
  ) => {
    field.onChange(data.value);
  };
  const handleTags = (tags: string[]) => {
    field.onChange(tags);
  };

  const errorMessage =
    formState.errors[field.name] &&
    (formState.errors[field.name] as any)?.message;

  switch (fieldInfo.type) {
    case FieldsType.bool: {
      return (
        <Form.Dropdown
          className={getClassName(field)}
          placeholder={`Enter your ${fieldInfo.name}`}
          label={fieldLabel}
          required={fieldInfo.required}
          selection
          name={fieldLabel}
          value={field.value}
          options={booleanDropdownOptions(fieldInfo.required)}
          onChange={handleBoolean}
          error={errorMessage}
        />
      );
    }
    case FieldsType.dateTime: {
      return (
        <DatePickerInput
          placeholder={`Enter your ${fieldInfo.name}`}
          label={fieldLabel}
          dateFormat={dateTimeFormat}
          onKeyDown={(date) => field.onChange(date)}
          value={field.value}
          onChange={(date) => field.onChange(date)}
          onBlur={field.onBlur}
          isMandatory={fieldInfo.required}
          isError={!!errorMessage}
          errorMessage={errorMessage}
        />
      );
    }
    case FieldsType.number: {
      return (
        <Form.Input
          placeholder={`Enter your ${fieldInfo.name}`}
          data-testid={`input-${field.name}`}
          required={fieldInfo.required}
          value={
            field.value != null && field.value != undefined ? field.value : ''
          }
          onChange={field.onChange}
          label={fieldLabel}
          fluid
          type="number"
          error={errorMessage}
        />
      );
    }
    case FieldsType.string: {
      if (fieldInfo.isArray) {
        return (
          <div
            data-testid={`input-${field.name}`}
            className={clsx({
              required: fieldInfo.required,
              error: errorMessage,
            })}
          >
            <Form.Field className="required-label" label={fieldLabel} />
            <POSMultiTag
              required={fieldInfo.required}
              items={
                field.value !== null && field.value !== undefined
                  ? field.value
                  : []
              }
              onChange={handleTags}
            />
          </div>
        );
      }
      return (
        <Form.Input
          placeholder={`Enter your ${fieldInfo.name}`}
          data-testid={`input-${field.name}`}
          required={fieldInfo.required}
          label={fieldLabel}
          value={field.value || ''}
          onChange={field.onChange}
          fluid
          type="text"
          error={errorMessage}
        />
      );
    }
    case FieldsType.xdm: {
      if (fieldInfo.xdm?.isArray) {
        return (
          <XdmMultiselectDropdown
            fieldInfo={fieldInfo}
            value={field.value || defaultValues || []}
            onChange={(entities: any[]) => {
              field.onChange(entities);
              onCustomFieldChange?.(entities, fieldInfo.name);
            }}
            errorMessage={errorMessage}
          />
        );
      }

      return (
        <Form.Input
          placeholder={`Enter your ${fieldInfo.name}`}
          data-testid={`input-${field.name}`}
          required={fieldInfo.required}
          value={''}
          onChange={field.onChange}
          label={fieldLabel}
          fluid
          type="text"
          error={errorMessage}
        />
      );
    }
    case FieldsType.xdmContext: {
      return isForFiltering
        ? getXdmContextItem(
            fieldInfo,
            field,
            fieldLabel,
            defaultValues,
            onCustomFieldChange
          )
        : getFieldForXdmContext(fieldInfo, field, fieldLabel);
    }
    case FieldsType.multiSelect: {
      return getFieldForMultiSelect(fieldInfo, field, errorMessage);
    }
    case FieldsType.singleSelect: {
      return getFieldForSingleSelect(fieldInfo, field);
    }
    default: {
      return <></>;
    }
  }
};
